import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatClockTime, formatSog } from '@boatnav/core';
import type { ShareGetResponse } from '@boatnav/schemas';
import { fetchShareView } from '../../lib/apiClient.js';
import { ViewerMap } from './ViewerMap.js';
import { msg } from '../../lib/messages.js';
import './viewer.css';

const POLL_MS = 15_000;

type ViewState =
  | { kind: 'loading' }
  | { kind: 'ok'; data: ShareGetResponse }
  | { kind: 'gone' }
  | { kind: 'not-found' };

/**
 * `/del/:id` (spec 15) — routed outside AppShell (no tabs, no account gate):
 * a stranger with the link never needs an account, never sends a cookie, and
 * never sees the rest of the app. Polls with a plain `setInterval` + `fetch`,
 * matching the old-browser floor (no dependency on a data-fetching library).
 */
export function ShareViewerScreen(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [state, setState] = useState<ViewState>({ kind: 'loading' });
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    async function poll(): Promise<void> {
      try {
        const res = await fetchShareView(id as string);
        if (cancelled) return;
        if ('status' in res) {
          setState(res.status === 404 ? { kind: 'not-found' } : { kind: 'gone' });
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
        } else {
          setState({ kind: 'ok', data: res });
        }
      } catch {
        // Offline/transient fetch failure — keep showing the last known state
        // and just try again on the next tick.
      }
    }

    void poll();
    timerRef.current = setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [id]);

  if (!id || state.kind === 'not-found') {
    return <ViewerMessage title={msg.share.viewer.notFound} body={msg.share.viewer.notFoundBody} />;
  }
  if (state.kind === 'gone') {
    return <ViewerMessage title={msg.share.viewer.ended} body={msg.share.viewer.endedBody} />;
  }
  if (state.kind === 'loading') {
    return <ViewerMessage title={msg.share.viewer.loading} body="" />;
  }

  const { data } = state;
  return (
    <div className="viewer-screen">
      <ViewerMap
        track={data.track}
        latest={data.latest ? { lat: data.latest.lat, lon: data.latest.lon } : null}
      />
      <div className="viewer-header">
        <span className="viewer-header__boat">{data.boatName ?? msg.share.viewer.boatFallback}</span>
        {data.latest && (
          <span className="viewer-header__meta">
            {msg.share.viewer.lastSeen} {formatClockTime(data.latest.ts)} · {formatSog(data.latest.sogKn)} kn
          </span>
        )}
      </div>
      <div className="viewer-footer">{msg.share.viewer.footer}</div>
    </div>
  );
}

function ViewerMessage({ title, body }: { title: string; body: string }): JSX.Element {
  return (
    <div className="viewer-screen viewer-screen--message">
      <div className="viewer-message">
        <h1 className="viewer-message__title">{title}</h1>
        {body && <p className="viewer-message__body">{body}</p>}
      </div>
      <div className="viewer-footer">{msg.share.viewer.footer}</div>
    </div>
  );
}
